import _ from 'lodash';

import { captureException, AUTHENTICATION_SCOPES } from '@playbookux/playbook-web-core';
import Graphql from '../services/graphql';
import { SCREENER_SESSION_ERROR, SCREENER_SESSION_STATUS } from '../constants/screener-sessions';
import { orderArray } from '../utils/order-array';
import { validateScreenerAnswers } from '../utils/validate-screener-answers';

import { GET_SCREENER_SESSION, CREATE_SCREENER_SESSION_ANSWER } from './screener-graphql';

import {
  SCREENER_SESSION_FETCH_CURRENT,
  SCREENER_SESSION_FETCH_CURRENT_COMPLETED,
  SCREENER_SESSION_FETCH_CURRENT_FAILED,
  SCREENER_SESSION_CURRENT_QUESTION,
  SCREENER_SESSION_NEXT_QUESTION,
  SCREENER_SESSION_QUESTIONS_ORDER,
  SCREENER_SESSION_RESET_SURVEY,
  SCREENER_SESSION_SET_ANSWERS,

  SCREENER_SESSION_SURVEY_COMPLETED,
  SCREENER_SESSION_SURVEY_UPLOAD_COMPLETED,
} from './action-types';

/**
 * @description Get current Screener Session
 * @param {string} sessionId
 */
export const fetchCurrentScreenerSession = (sessionId, accessToken) => async (dispatch) => {
  dispatch({
    type: SCREENER_SESSION_FETCH_CURRENT,
  });

  try {
    const query = Graphql;
    query.setToken(accessToken);
    query.setScope(AUTHENTICATION_SCOPES.TESTER);

    const { data } = await query.query({
      query: GET_SCREENER_SESSION,
      variables: {
        sessionId,
      },
    });

    let id = null;
    let tester = {};
    let project = {};
    let segment = {};
    let surveys = {};

    const { screenerSession } = data;
    ({ id, tester } = screenerSession);
    ({ project, segment, ...tester } = tester);
    ({ surveys } = segment);
    ({ ...project } = project);

    const questions = _.chain(surveys)
      .filter((s) => s.surveyType === 'screener')
      .map(((s) => s.questions))
      .first()
      .value();

    // FIXME: Need to omit __typename from GraphQL objects
    const payload = {
      id,
      tester,
      project,
      segment,
      questions,
      sessionId,
      accessToken,
    };

    dispatch({
      type: SCREENER_SESSION_FETCH_CURRENT_COMPLETED,
      payload,
    });

    return payload;
  } catch (err) {
    captureException('FETCH_CURRENT_SCREENER_SESSION', err);
    dispatch({
      type: SCREENER_SESSION_FETCH_CURRENT_FAILED,
    });

    throw err;
  }
};

export const screenerSurveyCompleted = (answers) => (dispatch) => {
  const payload = {
    answers,
  };

  dispatch({
    type: SCREENER_SESSION_SURVEY_COMPLETED,
    payload,
  });

  return payload;
};

/**
 * @description Save screener session answers
 */
export const saveScreenerAnswers = (values, cintId = null, mrId = null) => async (dispatch, getState) => {
  const { currentQuestion, formValue } = getState().screenerSessionAnswers;
  const { sessionId, accessToken } = formValue;

  const keyAnswers = Object.keys(values);
  const isAnswered = !_.isEmpty(values) && values[currentQuestion.id];

  const attributes = [];

  let screenerSessionAnswers = [];

  keyAnswers.forEach((question) => {
    // convert to key value array
    const response = _.map(_.toPairs(values[question].value), (d) => _.fromPairs([d]));

    // filter only selected
    const filtered = _.filter(
      response, (answer) => _.some(
        answer, (selected) => _.isBoolean(selected) && selected,
      ),
    );

    const answers = Object.keys(values[question].value);

    if (answers.length > 1) {
      filtered.forEach((answer) => (
        attributes.push({
          screenerSessionId: sessionId,
          questionId: question,
          questionOptionId: _.keys(answer)[0],
        })
      ));
    } else {
      attributes.push({
        screenerSessionId: sessionId,
        questionId: question,
        questionOptionId: answers[0],
      });
    }
  });

  if (isAnswered) {
    const graphql = Graphql;
    graphql.setToken(accessToken);
    graphql.setScope(AUTHENTICATION_SCOPES.TESTER);

    try {
      const { data = {} } = await graphql.mutate({
        mutation: CREATE_SCREENER_SESSION_ANSWER,
        variables: {
          attributes,
          cintId,
          mrId,
        },
      });

      const { createScreenerSessionAnswer } = data;

      const { tester, testSession } = createScreenerSessionAnswer;

      if (tester && tester.status === 'disqualified') {
        return 'disqualified';
      }

      if (testSession) {
        return testSession;
      }

      screenerSessionAnswers = createScreenerSessionAnswer
        .screenerSessionAnswer.map((answer) => answer.id);
    } catch (error) {
      // Validate if the error is for outdated screener survey
      const errorExtensionsCode = error?.graphQLErrors?.map((err) => err?.extensions?.code);

      if (errorExtensionsCode.includes(SCREENER_SESSION_ERROR.OUTDATED)) {
        return SCREENER_SESSION_STATUS.OUTDATED;
      }

      // Map the errors to an array as it was before
      const errorsArray = error?.graphQLErrors?.map((err) => err.message);

      return errorsArray;
    }

    dispatch({
      type: SCREENER_SESSION_SURVEY_UPLOAD_COMPLETED,
    });
  }

  return screenerSessionAnswers;
};

// eslint-disable-next-line consistent-return
export const verifyCurrentAnswer = (values) => async (dispatch, getState) => {
  const { sessionId, accessToken } = getState().screenerSessionAnswers.formValue;

  const query = Graphql;
  query.setToken(accessToken);
  query.setScope(AUTHENTICATION_SCOPES.TESTER);

  const { data } = await query.query({
    query: GET_SCREENER_SESSION,
    variables: {
      sessionId,
    },
  });

  const { screenerSession } = data;
  const { tester } = screenerSession;
  const { segment } = tester;
  const { surveys } = segment;

  const questions = _.chain(surveys)
    .filter((s) => s.surveyType === 'screener')
    .map(((s) => s.questions))
    .first()
    .value();

  const { currentQuestion } = getState().screenerSessionAnswers;

  const filteredQuestion = _.chain(questions)
    .filter((s) => s.id === currentQuestion.id)
    .first()
    .value();

  // If the question is outdated, the survey that the question belongs to is also outdated
  // filterQuestion will be undefined, because the questionId will not be found in the most recent survey version
  if (!filteredQuestion) {
    return SCREENER_SESSION_STATUS.OUTDATED;
  }

  const { questionOptions } = filteredQuestion;
  const keyAnswers = Object.keys(values.value);

  return validateScreenerAnswers(keyAnswers, questionOptions);
};

export const setScreenerSessionQuestions = (questions, fieldOrder, randomize) => async (dispatch) => {
  const orderedQuesions = orderArray(questions, fieldOrder, randomize);

  dispatch({
    type: SCREENER_SESSION_QUESTIONS_ORDER,
    payload: { questions: orderedQuesions },
  });
};

export const setScreenerSessionNextQuestion = (currentQuestion) => async (dispatch) => {
  dispatch({
    type: SCREENER_SESSION_NEXT_QUESTION,
    payload: { currentQuestion },
  });
};

export const setScreenerSessionCurrentQuestion = (questionId) => async (dispatch) => {
  dispatch({
    type: SCREENER_SESSION_CURRENT_QUESTION,
    payload: { questionId },
  });
};

export const resetScreener = () => (dispatch) => {
  dispatch({
    type: SCREENER_SESSION_RESET_SURVEY,
  });
};

export const setScreenerSessionAnswers = (answers) => async (dispatch) => {
  dispatch({
    type: SCREENER_SESSION_SET_ANSWERS,
    payload: { answers },
  });
};
